import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { parseAdd, pickUrl } from '../catalog/pick';
import { useUrlState } from '../nav/urlState';
import type { AddGear } from '../quotes/AddPricedLine';
import { useSupplierSwitch } from '../quotes/supplierSwitch';
import { useCatalogOpener } from '../nav/catalogLink';
import { api, ApiError } from '../api';
import { can, fieldVisible, fieldWritable } from '../auth/me';
import { useMe } from '../auth/useMe';
import { categoryLabels, useCategories } from '../catalog/ItemForm';
import { useIsPhone } from '../events/EventsPage';
import { useGoToQuote, type GoToQuoteNotice } from '../quotes/GenerateMenu';
import { QUOTES_KEY } from '../quotes/types';
import { ConflictNotice } from '../ui/ConflictNotice';
import { RowMenu, type RowAction } from '../ui/RowMenu';
import { ApplyMenu, mayApply } from './ApplyMenu';
import { CopyFromEventDialog } from './CopyFromEventDialog';
import { MergeDialog } from './MergeDuplicates';
import { PacklistSheet } from './PacklistSheet';
import { PDF_SIZES, pdfUrl, storedPdfSize, storePdfSize, type PdfSize } from './pdfSize';
import { RulePanel, SkipRuleDialog } from './RulePanel';
import { SaveAsDialog } from './SaveAsDialog';
import { SystemBlockHint } from './SystemBlockHint';
import { duplicateGroups, PACKLISTS_RESOURCE, packlistKey, requiredRulesOf, type LineView, type PacklistView, type VersionView } from './types';
import { mayMoveSupplier, OldVersionSheet, type PlacedSupplierLine, PickupsDiffer, SupplierSections, useFromQuote, useVersionActions, VersionSwitch } from './versions';
import { RentalForm } from '../subrentals/RentalForm';
import { useRentalReturn } from '../subrentals/rentalPick';
import { presetOf, RENTALS_RESOURCE, rentalsKey, type RentalPreset, type RentalView } from '../subrentals/types';
import { rentedOfItem } from '../subrentals/rented';
import './packlists.css';
import { DialogLayer } from '../ui/Dialog';
import { HiddenUndo, SupplierRemoveDialog, type HiddenLine } from './SupplierRemove';
import { Notice } from '../ui/Notice';

/** Where the name of a contribution's source is read. */
const SOURCE_URL = { kit: '/api/kits/', block: '/api/templates/blocks/', template: '/api/templates/templates/' } as const;

/**
 * The packlist editor: one table edited in place, like the PDF and the quote (owner remark 19). It is the event card's
 * tab "Sprzętówka" (owner 2026-10-21 23:31: "normalna zakładka, w której będą pozycje"; "Pełny widok" spreads it
 * over the window) and the screen `/wydarzenia/:id/sprzetowka`. Owner 23:36–23:39 ("walczymy o porządek i brak
 * rozpraszaczy"): on top only "Przejdź do kosztorysu"; the rest sits in the top-right corner — "…" (insert a block or
 * template, copy from an event, merge repeats, PDF, loading, save as a template) and ⚙ (the PDF's font). Positions are
 * added in the table's own rows. On a phone the table is for reading.
 */
export function PacklistEditor({ eventId, title, onGoToQuote }: { eventId: string; /** The left side of the header (the screen's title). */ title?: ReactNode; /** Opens the quote (the card's tab or the card). */ onGoToQuote?: ((notice: GoToQuoteNotice) => void) | undefined }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // owner 2026-10-23 20:53: Ctrl+klik (Windows: Alt+klik) on a line opens it in the catalog with the editor open
  const openInCatalog = useCatalogOpener();
  const phone = useIsPhone();
  const [removing, setRemoving] = useState<LineView | null>(null);
  /** Owner 2026-10-24 20:36: the supplier's line whose × was clicked. */
  const [removingSupplier, setRemovingSupplier] = useState<PlacedSupplierLine | null>(null);
  /** Review M2: a line just hidden „tylko ze sprzętówki”, for its „Cofnij”. */
  const [hiddenLine, setHiddenLine] = useState<HiddenLine | null>(null);
  const [pdfSize, setPdfSize] = useState<PdfSize | null>(null);
  const list = useQuery({ queryKey: packlistKey(eventId), queryFn: () => api<PacklistView>(`/api/packlists/${eventId}`) });
  const goToQuote = useGoToQuote(me, eventId, phone, onGoToQuote);
  const lines = list.data?.lines ?? [];
  // versions (owner 2026-10-22 14:29): the current one is edited; an older one is shown read-only
  const currentVersion = list.data?.currentVersion ?? 1;
  const [shownVersion, setShownVersion] = useState<number | null>(null);
  const shown = shownVersion !== null && (list.data?.versions ?? []).some((v) => v.number === shownVersion && !v.current) ? shownVersion : null;
  const older = useQuery({
    queryKey: [...packlistKey(eventId), 'version', shown],
    enabled: shown !== null,
    queryFn: () => api<VersionView>(`/api/packlists/${eventId}/versions/${shown}`),
  });
  const [versionNotice, setVersionNotice] = useState('');
  const versionActions = useVersionActions(
    eventId,
    (text) => {
      setShownVersion(null);
      setVersionNotice(text);
    },
    () => setVersionNotice(''),
  );
  const fromQuote = useFromQuote(me, eventId, !phone, (text) => {
    setShownVersion(null);
    setVersionNotice(text);
  });
  const categories = useCategories(!!me && can(me, 'catalog.categories', 'view'));

  // The API sends no names of kits, blocks and templates; they are fetched by id, and a role that may not see them
  // gets the plain word ("zestaw", "blok", "szablon").
  const sources = [
    ...new Map(
      lines.flatMap((l) => l.contributions.flatMap((c) => (c.sourceId && c.source in SOURCE_URL ? [[c.sourceId, c.source as keyof typeof SOURCE_URL] as const] : []))),
    ),
  ].sort(([a], [b]) => (a < b ? -1 : 1));
  const named = useQueries({
    queries: sources.map(([id, source]) => ({
      queryKey: [source === 'kit' ? 'kits' : 'templates', 'name', id],
      retry: false,
      staleTime: 60_000,
      queryFn: () => api<{ name: string }>(`${SOURCE_URL[source]}${id}`),
    })),
  });
  const sourceNames = new Map<string, string>();
  sources.forEach(([id], i) => {
    const name = named[i]?.data?.name;
    if (name) sourceNames.set(id, name);
  });
  // rule names come with the packlist
  for (const [id, name] of Object.entries(list.data?.ruleNames ?? {})) sourceNames.set(id, name);
  const ruleOutcomes = list.data?.ruleOutcomes ?? [];
  /** "Usuń" of a line a required rule keeps: skip the rule instead (no reason, owner 2026-10-24). */
  const [skipping, setSkipping] = useState<{ line: LineView; ruleIds: string[] } | null>(null);
  const askRemove = (line: LineView) => {
    const ruleIds = requiredRulesOf(line, ruleOutcomes);
    if (ruleIds.length > 0) setSkipping({ line, ruleIds });
    else setRemoving(line);
  };
  /** "Zapisz jako blok": the chosen lines (ids no longer on the list are ignored). */
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const chosen = lines.filter((l) => selected.has(l.id)).map((l) => l.id);
  const [saveAs, setSaveAs] = useState<'template' | 'block' | null>(null);
  /** F28: "Dopożycz" at a shortage opens the rental form filled in. */
  const [subrent, setSubrent] = useState<RentalPreset | null>(null);
  // „Dopożycz” open again after „Wskaż w katalogu” (owner 2026-10-23 21:23): it lives in the address
  const rentalReturn = useRentalReturn();
  // owner 2026-10-23 23:35: on hover, from whom the item is rented (the API filters quantities and suppliers, rule 7a)
  const rentals = useQuery({
    queryKey: rentalsKey(eventId),
    queryFn: () => api<{ rentals: RentalView[] }>(`/api/subrentals/events/${eventId}/rentals`),
    enabled: !!me && can(me, RENTALS_RESOURCE, 'view'),
  });
  const [savedNotice, setSavedNotice] = useState('');
  /** "Kopiuj z wydarzenia" (owner 2026-10-21 06:58): the dialog, then what it did. */
  const [copying, setCopying] = useState(false);
  const [copyNotice, setCopyNotice] = useState('');
  /** "Wstaw blok lub szablon" and "Scal powtórzenia", opened from "…". */
  const [applying, setApplying] = useState(false);
  const [merging, setMerging] = useState(false);
  // owner 2026-10-24 11:15 („tak samo jak w kosztorysie”): the ⚙ of „Dodaj…” — the suppliers' switch shared with the
  // quote, and „Wybierz z katalogu”, which comes back here with `dodaj=item:<id>` / `dodaj=dostawca:<priceId>`
  const location = useLocation();
  const [useSuppliers, setUseSuppliers] = useSupplierSwitch();
  const { params, update } = useUrlState();
  const toAdd = parseAdd(params);
  const picked = useRef<string | null>(null);
  const [pickError, setPickError] = useState('');
  const mayEditList = !!me && can(me, PACKLISTS_RESOURCE, 'edit') && !phone;
  useEffect(() => {
    // a quote's pick always names its section: never this packlist's
    if (!toAdd || toAdd.sectionId !== '' || !me) return;
    // review: without the right to change the packlist the pick is dropped (never left for the quote to post)
    if (!mayEditList) {
      update({ dodaj: null });
      return;
    }
    const key = `${toAdd.kind}:${toAdd.id}`;
    if (picked.current === key) return;
    picked.current = key;
    const done =
      toAdd.kind === 'item'
        ? api(`/api/packlists/${eventId}/lines`, { method: 'POST', body: { itemId: toAdd.id, quantity: 1 } })
        : api(`/api/quotes/events/${eventId}/packlist-supplier-lines`, { method: 'POST', body: { priceId: toAdd.id, quantity: 1 } });
    done
      .then(() => setPickError(''))
      .catch((e: unknown) => setPickError(e instanceof ApiError ? `Nie udało się dodać wybranej pozycji: ${e.message}` : 'Nie udało się dodać wybranej pozycji'))
      .finally(() => {
        update({ dodaj: null, sekcja: null });
        void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
        void queryClient.invalidateQueries({ queryKey: rentalsKey(eventId) });
      });
  }, [toAdd, me, mayEditList, eventId, update, queryClient]);

  const remove = useMutation({
    mutationFn: (line: LineView) => api(`/api/packlists/lines/${line.id}?version=${line.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setRemoving(null);
      await queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
    },
  });
  const removeError = remove.error instanceof ApiError ? remove.error : null;
  const reload = () => {
    remove.reset();
    setRemoving(null);
    void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
  };

  if (!me) return null;
  const canEdit = can(me, PACKLISTS_RESOURCE, 'edit');
  const editable = canEdit && !phone;
  const withQuantity = lines.some((l) => l.quantity !== undefined);
  const canSaveBlock = editable && can(me, 'templates.blocks', 'create') && withQuantity;
  const canSaveTemplate = editable && can(me, 'templates.templates', 'create') && withQuantity;
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const size = pdfSize ?? storedPdfSize(me.id);
  // the quote's rule (rule 7a): who sees the costs and the suppliers' prices and may rent works with suppliers
  const supplierRights =
    editable &&
    can(me, 'finance.profitability', 'view') &&
    can(me, 'finance.profitability', 'edit') &&
    can(me, 'subrentals.prices', 'view') &&
    fieldVisible(me, 'subrentals.prices', 'priceDayGrosze') &&
    fieldVisible(me, RENTALS_RESOURCE, 'costGrosze') &&
    can(me, RENTALS_RESOURCE, 'create');
  const gear: AddGear | undefined = editable
    ? {
        canSuppliers: supplierRights,
        useSuppliers: supplierRights && useSuppliers,
        onToggleSuppliers: () => setUseSuppliers(!useSuppliers),
        onInsertBlock: mayApply(me) ? () => setApplying(true) : undefined,
        onPickCatalog: () => navigate(pickUrl(supplierRights && useSuppliers ? '/katalog/dostawcy' : '/katalog', { eventId, back: `${location.pathname}${location.search}` })),
      }
    : undefined;
  const duplicates = duplicateGroups(lines);
  const versionCount = (list.data?.versions ?? []).length;
  // "…" of an older version: bring it back, or delete it (owner 14:29: "dopóki ich nie usunę")
  const olderMenu: RowAction[] =
    shown !== null && editable
      ? [
          { label: 'Przywróć jako aktualną', onSelect: () => versionActions.run({ kind: 'restore', number: shown, current: currentVersion }) },
          { label: `Usuń wersję v${shown}`, onSelect: () => versionActions.run({ kind: 'delete', number: shown, current: currentVersion }) },
        ]
      : [];
  const hasSomething = lines.length > 0 || (list.data?.suppliers ?? []).length > 0;
  // "…": the rarer actions, the editing ones first (owner 23:39: "wstaw blok też w trzech kropkach")
  const more: RowAction[] = shown !== null ? olderMenu : [
    ...(editable && fromQuote.action ? [fromQuote.action] : []),
    ...(editable && mayApply(me) ? [{ label: 'Wstaw blok lub szablon…', onSelect: () => setApplying(true) }] : []),
    ...(editable ? [{ label: 'Kopiuj z wydarzenia…', onSelect: () => setCopying(true) }] : []),
    ...(editable && duplicates.length > 0 ? [{ label: `Scal powtórzenia (${duplicates.length})…`, onSelect: () => setMerging(true) }] : []),
    ...(canSaveTemplate ? [{ label: 'Zapisz jako szablon…', onSelect: () => setSaveAs('template') }] : []),
    { label: 'Pobierz PDF', onSelect: () => window.location.assign(pdfUrl(eventId, size)) },
    // owner 23:37 (b): the return of the gear is reached from "Dopożyczenia" and from the loading screen
    ...(canEdit ? [{ label: 'Załadunek', onSelect: () => navigate(`/wydarzenia/${eventId}/zaladunek`) }] : []),
    // the open (current) version; the only one with nothing in it has nothing to delete
    ...(editable && (versionCount > 1 || hasSomething) ? [{ label: `Usuń wersję v${currentVersion}`, onSelect: () => versionActions.run({ kind: 'delete', number: currentVersion, current: currentVersion }) }] : []),
  ];
  // ⚙ (owner 23:36: "rozmiar czcionki w małym trybiku"): the PDF's font, remembered on this device
  const settings: RowAction[] = PDF_SIZES.map((s) => ({
    label: `Czcionka PDF: ${s.label.toLocaleLowerCase('pl-PL')}`,
    checked: s.value === size,
    onSelect: () => {
      setPdfSize(s.value);
      storePdfSize(me.id, s.value);
    },
  }));

  return (
    <div className="pl-editor-body" aria-label="Edytor sprzętówki" role="region">
      <div className="pl-bar">
        <div className="pl-bar-title">{title}</div>
        <div className="pl-bar-end">
          {goToQuote.show && (
            <button className="btn small primary" type="button" data-feature-id="quotes.from_packlist" disabled={goToQuote.busy} onClick={goToQuote.run}>
              {goToQuote.busy ? 'Przygotowuję kosztorys…' : 'Przejdź do kosztorysu'}
            </button>
          )}
          {editable && canSaveBlock && chosen.length > 0 && (
            <button className="btn small" type="button" data-feature-id="packlists.save_as" onClick={() => setSaveAs('block')}>
              Zapisz jako blok ({chosen.length})
            </button>
          )}
          <RowMenu label="Więcej działań sprzętówki" featureId="packlists.save_as" actions={more} />
          <RowMenu icon="gear" label="Ustawienia sprzętówki" featureId="packlists.pdf" actions={settings} />
        </div>
      </div>
      {goToQuote.error && (
        <p className="alert" role="alert">
          {goToQuote.error}
        </p>
      )}
      {goToQuote.view}
      {fromQuote.view}
      <VersionSwitch versions={list.data?.versions ?? []} shown={shown ?? currentVersion} onShow={(n) => setShownVersion(n === currentVersion ? null : n)} />
      {versionActions.error && (
        <p className="alert" role="alert">
          {versionActions.error}
        </p>
      )}
      {versionNotice && (
        <Notice onClose={() => setVersionNotice('')}>
          {versionNotice}
        </Notice>
      )}
      {applying && editable && <ApplyMenu eventId={eventId} onClose={() => setApplying(false)} />}
      {phone && canEdit && <p className="muted pl-phone-note">Na telefonie sprzętówkę można przejrzeć; edycja w tabeli jest na komputerze.</p>}
      {shown === null && <SystemBlockHint eventId={eventId} packlist={list.data} canApply={editable && can(me, 'templates.blocks', 'view')} />}
      {shown === null && <RulePanel eventId={eventId} suggestions={list.data?.suggestions ?? []} reminders={list.data?.reminders ?? []} canEdit={editable} />}
      {savedNotice && (
        <Notice onClose={() => setSavedNotice('')}>
          {savedNotice} <Link to={savedNotice.startsWith('Zapisano blok') ? '/szablony/bloki' : '/szablony'}>Szablony</Link>
        </Notice>
      )}
      {pickError && (
        <p className="alert" role="alert">
          {pickError}
        </p>
      )}
      {copyNotice && (
        <Notice onClose={() => setCopyNotice('')}>
          {copyNotice}
        </Notice>
      )}
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać sprzętówki'}</p>}
      {shown !== null && (
        <div className="pl-old" data-feature-id="packlists.versions">
          <p className="notice pl-old-note" role="status">
            Wersja v{shown} — starsza, tylko do odczytu. Aktualna jest v{currentVersion}.
          </p>
          {older.isLoading && <p className="muted">Wczytywanie…</p>}
          {older.error && <p className="alert" role="alert">{older.error instanceof ApiError ? older.error.message : 'Nie udało się wczytać wersji'}</p>}
          {older.data && (
            <>
              <OldVersionSheet
                lines={older.data.lines}
                cols={{ quantity: fieldVisible(me, PACKLISTS_RESOURCE, 'quantity'), window: fieldVisible(me, PACKLISTS_RESOURCE, 'firstDay'), notes: fieldVisible(me, PACKLISTS_RESOURCE, 'notes') }}
              />
              <SupplierSections sections={older.data.suppliers} editable={false} showQuantity={fieldVisible(me, PACKLISTS_RESOURCE, 'quantity')} onChanged={() => undefined} />
            </>
          )}
        </div>
      )}
      {shown === null && list.data && lines.length === 0 && !editable && (list.data.suppliers ?? []).length === 0 && <p className="muted">Sprzętówka jest pusta.</p>}
      {shown === null && list.data && (
        <PacklistSheet
          onOpenCatalog={can(me, 'catalog.items', 'view') ? (itemId, name) => openInCatalog({ kind: 'item', itemId }, name) : undefined}
          me={me}
          eventId={eventId}
          lines={lines}
          categories={categoryLabels(categories.data?.categories ?? [])}
          sourceNames={sourceNames}
          ruleOutcomes={ruleOutcomes}
          editable={editable}
          cols={{
            select: canSaveBlock,
            quantity: fieldVisible(me, PACKLISTS_RESOURCE, 'quantity'),
            window: fieldVisible(me, PACKLISTS_RESOURCE, 'firstDay'),
            notes: fieldVisible(me, PACKLISTS_RESOURCE, 'notes'),
          }}
          selected={selected}
          onToggleSelect={toggle}
          onRemove={askRemove}
          onReload={reload}
          gear={shown === null ? gear : undefined}
          {...(can(me, RENTALS_RESOURCE, 'create') ? { onSubrent: (l: LineView) => setSubrent(presetOf(l, list.data?.event)) } : {})}
          rentedOf={Array.isArray(rentals.data?.rentals) ? (itemId: string) => rentedOfItem(rentals.data!.rentals, itemId) : undefined}
          // owner 2026-10-24 („ma być jak w kosztorysie”): the suppliers' gear in its category groups, not below
          suppliers={list.data.suppliers ?? []}
          onOpenSupplierCatalog={can(me, 'subrentals.prices', 'view') ? openInCatalog : undefined}
          supplierEditable={editable && mayMoveSupplier(me)}
          // owner 2026-10-24 20:36: the × — the packlist's „Ilość” written; the own position's rental goes too (its delete right)
          onSupplierRemove={setRemovingSupplier}
          maySupplierRemove={(p) => editable && !!p.line.id && fieldWritable(me, PACKLISTS_RESOURCE, 'quantity') && (!!p.line.quoteLineId || can(me, RENTALS_RESOURCE, 'delete'))}
          onSupplierChanged={() => {
            // the quote line and its rental moved too (the quote's way)
            void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
            void queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
          }}
        />
      )}
      {hiddenLine && <HiddenUndo key={hiddenLine.undoId} eventId={eventId} hidden={hiddenLine} onClose={() => setHiddenLine(null)} />}
      {shown === null && list.data && <PickupsDiffer me={me} eventId={eventId} differs={!!list.data.suppliersDiffer} follows={list.data.suppliersFollow !== false} />}
      {merging && <MergeDialog eventId={eventId} lines={lines} groups={duplicates} onClose={() => setMerging(false)} />}
      {copying && (
        <CopyFromEventDialog
          eventId={eventId}
          onClose={() => setCopying(false)}
          onCopied={(notice) => {
            setCopyNotice(notice);
            setCopying(false);
          }}
        />
      )}
      {subrent && <RentalForm eventId={eventId} preset={subrent} onClose={() => setSubrent(null)} />}
      {!subrent && rentalReturn && <RentalForm eventId={eventId} preset={rentalReturn.preset} picked={rentalReturn.picked} onClose={rentalReturn.close} />}
      {saveAs && (
        <SaveAsDialog
          eventId={eventId}
          mode={saveAs}
          lineIds={chosen}
          onClose={() => setSaveAs(null)}
          onSaved={(name) => {
            setSavedNotice(saveAs === 'template' ? `Zapisano szablon „${name}”.` : `Zapisano blok „${name}”.`);
            if (saveAs === 'block') setSelected(new Set());
            setSaveAs(null);
          }}
        />
      )}
      {skipping && <SkipRuleDialog eventId={eventId} line={skipping.line} ruleIds={skipping.ruleIds} ruleNames={list.data?.ruleNames ?? {}} onClose={() => setSkipping(null)} />}
      {removingSupplier && <SupplierRemoveDialog eventId={eventId} placed={removingSupplier} onClose={() => setRemovingSupplier(null)} onHidden={setHiddenLine} />}
      {removing && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="remove-line-title">
            <DialogLayer onClose={() => { setRemoving(null); remove.reset(); }} busy={remove.isPending} />
            <h2 id="remove-line-title">Usunąć ze sprzętówki?</h2>
            <p>„{removing.itemName}”{removing.quantity !== undefined ? ` (${removing.quantity})` : ''} zniknie ze sprzętówki, a rezerwacja zostanie zwolniona.</p>
            {removeError?.status === 409 && <ConflictNotice onReload={reload} />}
            {removeError && removeError.status !== 409 && (
              <p className="alert" role="alert">
                {removeError.message}
              </p>
            )}
            <div className="actions">
              <button className="btn primary" type="button" autoFocus disabled={remove.isPending} onClick={() => remove.mutate(removing)}>
                {remove.isPending ? 'Usuwanie…' : 'Usuń ze sprzętówki'}
              </button>
              <button
                className="btn"
                type="button"
                onClick={() => {
                  setRemoving(null);
                  remove.reset();
                }}
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
